import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {
  DragDropContext,
  DragHandle,
} from '@wandb/weave/common/containers/DragDropContainer';
import {makeDidPropsOrStateChange} from '@wandb/weave/common/util/shouldUpdate';
import {Button} from '@wandb/weave/components/Button';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import classNames from 'classnames';
import _, {cloneDeep} from 'lodash';
import React, {
  FC,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';
import {twMerge} from 'tailwind-merge';

import {ViewSource} from '../../generated/graphql';
import {useIsInLoadedReportContext} from '../../pages/ReportPage/LoadedReportContext';
import {useApolloClient, useDispatch} from '../../state/hooks';
import {saveView} from '../../state/reports/hooks';
import {usePanelComments} from '../../state/reports/usePanelComments';
import {RunQueryContext} from '../../state/runs/context';
import * as RunHooks from '../../state/runs/hooks';
import {useViewer} from '../../state/viewer/hooks';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../state/views/panelBankSectionConfig/actions';
import {useRunSets} from '../../state/views/runSet/hooks';
import {DateFormat, format} from '../../util/date';
import {
  EMPTY_PANEL_BANK_CONFIG,
  EMPTY_PANEL_BANK_SECTION_CONFIG_FOR_REPORT,
} from '../../util/panelbankConfigs';
import {isMobile} from '../../util/panelbankFlow';
import {getFullWidthPanelLayout} from '../../util/panelbankGrid';
import {ImageExportType} from '../../util/panelExport';
import {PanelCompRedux} from '../../util/panels';
import {getPanelSpec} from '../../util/panelTypes';
import {
  useRampFlagMediaPanelSettingsDrawer,
  useRampFlagMetricChangeNotifications,
} from '../../util/rampFeatureFlags';
import * as Report from '../../util/report';
import {truncateString} from '../../util/runhelpers';
import {useFeatureFlagVegaEnabled} from '../../util/useFeatureFlag';
import {TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {useDefinedMetricContext} from '../PanelBank/DefinedMetricContext';
import PanelEditor from '../PanelEditor';
import {PanelImageExportModal} from '../PanelImageExportModal';
import {useGetIsMediaPanelSettingsDrawerSupportedForPanel} from '../PanelMediaBrowser/useGetIsMediaPanelSettingsDrawerSupportedForPanel';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {usePanelsSharedRefsContext} from '../Views/contexts/PanelsSharedRefsContext';
import {RunColorOptions, Settings} from '../WorkspaceDrawer/Settings/types';
import {
  useWorkspaceDrawerContextMaybe,
  WorkspaceDrawerContextType,
} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {useConfigXBounds, useDetectPanelVariant, useGoToBaseUrl} from './hooks';
import {StyledButtonWrapper} from './PanelActions.style';
import {ExportType, PanelActionsMenu} from './PanelActionsMenu';
import {usePanelContext} from './PanelContextProvider';
import {PanelExportAPI} from './PanelExportAPI';
import {PanelExportCSV} from './PanelExportCSV';
import {PanelIDContextProvider} from './PanelIDContext';
import {SharedPanelStateContextProvider} from './SharedPanelStateContext';
import {ShareReportTriggerWrapper} from './ShareReportTriggerWrapper';
import {useSharePanelLink} from './StandalonePanel';
import {PanelPopupMode, PanelProps} from './types';

// report names are limited to 128 chars. When editing report titles we prevent
// users from exceeding this. But here, we dont have a limit on the title length
// and title length is used to create the report name. So we need to truncate
// also actual limit is 128, but truncateString uses single char elipses which is
// 3 chars in backend
const MAX_BACKEND_TITLE_LENGTH = 128 - 2;

const EditablePanelComp: FC<PanelProps> = ({
  currentHeight,
  defaultMoveToSectionRef,
  disableRunLinks,
  onContentHeightChange,
  shouldRenderContent = true,
  shouldTurnOnComments,
}) => {
  const {
    customRunColorsRef,
    customRunNamesRef,
    panelBankConfigRef,
    runSetRefs,
    workspaceSettingsRef,
    semanticLegendSettings,
  } = usePanelsSharedRefsContext();
  const {
    isReadOnly,
    panelBankSectionConfigRef,
    panelRef,
    panel,
    inheritedSettings,
    panelPopupMode,
    setPanelPopupMode,
    trackPanelControls,
  } = usePanelContext();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const {isFullScreenPanelPage} = useDetectPanelVariant();

  const panelId = searchParams.get('panelId');
  const isReport = useIsInLoadedReportContext();

  const goToBaseURL = useGoToBaseUrl();

  // Open panel editor only if panelId matches the current panel's ID
  useEffect(() => {
    if (isReport) {
      return;
    }
    if (panelId === panel.__id__) {
      setPanelPopupMode(PanelPopupMode.FULL_SCREEN);
    } else {
      setPanelPopupMode(undefined);
    }
  }, [panelId, panel.__id__, setPanelPopupMode, isReport]);

  const panelType = panel.viewType;
  const panelSpec = getPanelSpec(panelType);
  const sharePanelLink = useSharePanelLink();

  const openPanelEditor = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (isReport) {
      searchParams.set('panelId', panel.__id__);
      const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
      history.push(newUrl);
      setPanelPopupMode(PanelPopupMode.FULL_SCREEN);
      return;
    }
    history.push(sharePanelLink());
    window.scrollTo(0, 0); // Prevent panels opened from lower in the workspace from appearing partially scrolled out of view.
  }, [panel.__id__, setPanelPopupMode, history, isReport, sharePanelLink]);

  const dispatch = useDispatch();
  const onRemovePanel = useMemo(() => {
    if (!panelBankSectionConfigRef) {
      return;
    }
    return () =>
      dispatch(
        PanelBankSectionConfigActions.deletePanel(
          panelBankSectionConfigRef,
          panelRef,
          panelBankConfigRef,
          workspaceSettingsRef
        )
      );
  }, [
    dispatch,
    panelBankConfigRef,
    panelBankSectionConfigRef,
    panelRef,
    workspaceSettingsRef,
  ]);

  let panelTitle: string | undefined;
  if (panelSpec && panelSpec.getTitleFromConfig && panel && panel.config) {
    panelTitle = panelSpec.getTitleFromConfig(panel.config as any);
  }

  const {panelCommentCount, openPanelComments, addComment, isHighlighted} =
    usePanelComments({panelRef});

  const runSets = useRunSets(runSetRefs);
  const customRunColors = ViewHooks.usePart(customRunColorsRef);
  const customRunNames = ViewHooks.usePartMaybe(customRunNamesRef);
  const {mergeFilters, runSetName} = useContext(RunQueryContext);
  const {entityName, projectName} = RunHooks.useRunSetsQuery({runSetRefs});

  const viewer = useViewer();
  const client = useApolloClient();

  const vegaPanelEnabled = useFeatureFlagVegaEnabled(entityName);

  const [reportID, setReportID] = useState<undefined | string>();
  const [reportName, setReportName] = useState('');

  const [exportType, setExportType] = useState<ExportType>();
  const [imageExportType, setImageExportType] = useState<ImageExportType>();
  const unsetExportType = useCallback(() => setExportType(undefined), []);

  const {dragRef} = useContext(DragDropContext);
  const isSelected = useMemo(
    () => dragRef?.id === panelRef.id,
    [panelRef, dragRef]
  );

  const panelViewTypeClass = useMemo(
    () => panelType.toLowerCase().replace(' ', '-') + '-container',
    [panelType]
  );
  const {definedGlobs: defineMetricGlobDefinitions} = useDefinedMetricContext();

  const panelClassNames = useMemo(() => {
    return classNames('editable-panel', panelViewTypeClass, {
      'editable-panel-markdown': panelType === 'Markdown Panel',
      'panel-selected': isSelected,
      'panel-highlighted': isHighlighted,
      'editable-panel-no-content': !shouldRenderContent,
    });
  }, [
    panelViewTypeClass,
    panelType,
    isSelected,
    isHighlighted,
    shouldRenderContent,
  ]);

  const viewerIsMemberOfTeam = viewer?.teams.edges.some(
    edge => edge.node.name === entityName
  );

  const sharePanel = useCallback(async () => {
    const report = Report.fromSection(
      {
        runSets,
        customRunColors,
        customRunNames,
        settings: inheritedSettings?.linePlot as Settings,
        panelBankConfig: cloneDeep(EMPTY_PANEL_BANK_CONFIG),
        panelBankSectionConfig: {
          ...cloneDeep(EMPTY_PANEL_BANK_SECTION_CONFIG_FOR_REPORT),
          panels: [{...panel, layout: getFullWidthPanelLayout()}],
        },
        semanticLegendSettings:
          semanticLegendSettings?.runColorOptions ===
          RunColorOptions.MetricBasedColors
            ? semanticLegendSettings
            : undefined,
      },
      {mergeFilters, runSetName, noSectionHeader: true},
      undefined,
      defineMetricGlobDefinitions
    );

    const dateFormat = DateFormat.YEAR_MONTH_DAY_HOUR_MINUTE_SECOND;
    const extraCharsLength = 3 + dateFormat.length; // 3 for ' ()'
    const maybeTruncatedPanelTitle =
      panelTitle != null
        ? truncateString(
            panelTitle,
            MAX_BACKEND_TITLE_LENGTH - extraCharsLength,
            TruncationType.Middle
          )
        : panelTitle;
    const name = `${maybeTruncatedPanelTitle} (${format(
      new Date(),
      dateFormat
    )})`;
    const savedView = await saveView({
      client,
      entityName,
      projectName,
      reportName: name,
      draft: false,
      config: report,
      createdUsing: ViewSource.WandbUiSharePanel,
    });
    if (savedView.id == null) {
      toast('Error occured while trying to share panel');
    } else {
      setReportID(savedView.id);
      setReportName(name);
    }

    trackPanelControls({
      action: 'Share panel clicked',
      sharedPanelReportId: savedView.id,
    });
  }, [
    client,
    customRunColors,
    customRunNames,
    defineMetricGlobDefinitions,
    entityName,
    mergeFilters,
    panel,
    inheritedSettings,
    panelTitle,
    projectName,
    runSetName,
    runSets,
    semanticLegendSettings,
    trackPanelControls,
  ]);

  const initialConfigState = ViewHooks.usePart(panelRef);
  const configXBounds = useConfigXBounds(panelRef);
  const [isOverflowMenuActive, setIsOverflowMenuActive] = useState(false);
  const workspaceDrawerContext = useWorkspaceDrawerContextMaybe();
  let openWorkspaceDrawer:
    | undefined
    | WorkspaceDrawerContextType['openWorkspaceDrawer'] = undefined;
  let workspaceDrawerTarget:
    | undefined
    | WorkspaceDrawerContextType['targetPanelRef'] = undefined;
  if (workspaceDrawerContext) {
    openWorkspaceDrawer = workspaceDrawerContext.openWorkspaceDrawer;
    workspaceDrawerTarget = workspaceDrawerContext.targetPanelRef;
  }
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = selectedAccount?.name ?? '';

  const {data: runMetricNotificationEnabled} =
    useRampFlagMetricChangeNotifications(orgName, entityName);

  const isMediaPanelSettingsDrawerEnabled =
    useRampFlagMediaPanelSettingsDrawer(entityName);
  const getIsMediaPanelSettingsDrawerSupportedForPanel =
    useGetIsMediaPanelSettingsDrawerSupportedForPanel(panel);

  const getPanelEditor = () => {
    if (panelPopupMode == null) {
      return;
    }

    // Inspector is an old prototype of panel multi-edit in a sidebar. Its not
    // used and was causing circular imports.
    // if (panelPopupMode === PanelPopupMode.EDIT && panelSpec.useInspector) {
    //   return (
    //     <SinglePanelInspectorContainer
    //       panelRef={panelRef}
    //       customRunColorsRef={customRunColorsRef}
    //       runSetRefs={runSetRefs}
    //       onClose={() => setPanelPopupMode(undefined)}
    //     />
    //   );
    // }

    const baseProps = {
      currentHeight,
      customRunColorsRef,
      onCancel: () => {
        setPanelPopupMode(undefined);
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete('panelId');
        history.replace(`${window.location.pathname}?${urlParams.toString()}`);
      },
      onOK: () => setPanelPopupMode(undefined),
      onRemove: onRemovePanel,
      panelRef,
      inheritedSettings,
      panelSpec,
      readOnly: isReadOnly,
      runSetRefs,
    };
    const panelPopupProps = {
      [PanelPopupMode.EDIT]: {
        initialEditingState: true,
        initialConfigState,
      },
      [PanelPopupMode.FULL_SCREEN]: {
        initialEditingState: false,
        disableRunLinks,
      },
      [PanelPopupMode.VIEW_SPEC]: {
        initialEditingState: true,
        initialConfigState: {editMode: true},
      },
    };

    return <PanelEditor {...baseProps} {...panelPopupProps[panelPopupMode]} />;
  };

  const isWorkspaceDrawerTarget = useMemo(
    () => _.isEqual(panelRef, workspaceDrawerTarget),
    [panelRef, workspaceDrawerTarget]
  );
  let dataTest = `editable-panel-${panelType}`;
  if (panelType === 'Run History Line Plot') {
    dataTest = `editable-panel-${
      panel.config.metrics?.[0] ?? 'unknown-x-axis'
    }`;
  }
  if (panelType === 'Media Browser') {
    dataTest = `editable-panel-${panel.config?.mediaKeys?.join('-')}`;
  }

  return (
    <PanelIDContextProvider stablePanelId={panel.__id__}>
      <SharedPanelStateContextProvider config={configXBounds}>
        {getPanelEditor()}
        <TailwindContents>
          <div
            className={twMerge(
              panelClassNames,
              isFullScreenPanelPage ? 'px-16 py-12' : 'px-12 pb-12 pt-4',
              isWorkspaceDrawerTarget &&
                'border-gold-350 shadow-[0px_0px_0px_4px_rgb(255,230,168)]'
            )}
            data-test={dataTest}>
            <div
              className={twMerge(
                'flex shrink-0',
                // since we have more real estable in full screen mode, we should add more spacing between panel header and panel content itself
                isFullScreenPanelPage
                  ? 'mb-18 min-h-[32px]'
                  : 'mb-4 min-h-[24px]',
                // on a regular workspace OR report where multiple panels are visible, we want to only show
                // the actionable left side items when the mouse is over the panel. it should always be visible in full screen where we only
                // show 1 panel
                !isFullScreenPanelPage && 'opacity-0 hover:opacity-100',
                // since we hide the actionable items until mouse hover on a regular workspace or report where we display multiple panels,
                // we still want to show the actionable items when overflow menu is active
                isOverflowMenuActive && 'opacity-100'
              )}>
              {!isReadOnly && !isFullScreenPanelPage && (
                <DragHandle
                  key={`draghandle-${panelRef.id}`}
                  className={twMerge(
                    // this determines the mouse grab area. we need to account for the padding around panels and panel actions
                    'absolute left-0 right-0 mx-8'
                  )}
                  partRef={panelRef}>
                  <Button
                    aria-label="Drag panel"
                    className="absolute left-0 top-0 m-0 cursor-grab active:cursor-grabbing"
                    icon="drag-grip"
                    size="small"
                    variant="ghost"
                  />
                </DragHandle>
              )}
              <div
                className={twMerge(
                  // when in full screen mode where only 1 panel is visible, we want to position the back button to the left
                  isFullScreenPanelPage && 'flex w-full justify-between',
                  // on a regular workspace OR report where multiple panels are visible, the actions should be rendered on top
                  // of the drag handle and there's no back button to consider
                  !isFullScreenPanelPage && 'absolute right-8'
                )}>
                {isFullScreenPanelPage && (
                  <Button
                    data-test="full-screen-back"
                    icon="back"
                    variant="ghost"
                    onClick={goToBaseURL}
                  />
                )}

                <div className={twMerge('flex')}>
                  {panelType === 'Run History Line Plot' &&
                    runMetricNotificationEnabled &&
                    openWorkspaceDrawer != null && (
                      <Tooltip
                        content={
                          <div className="flex items-center gap-4">
                            Create alert automation
                          </div>
                        }
                        position="top center"
                        trigger={
                          <StyledButtonWrapper>
                            <Button
                              icon="bell-notifications"
                              variant="ghost"
                              aria-label="Create alert automation"
                              size={isFullScreenPanelPage ? 'medium' : 'small'}
                              onClick={() => {
                                openWorkspaceDrawer?.(
                                  'create-trigger',
                                  undefined,
                                  panelRef
                                );
                              }}
                            />
                          </StyledButtonWrapper>
                        }
                      />
                    )}
                  {/* View spec */}
                  {isReadOnly && panelType === 'Vega2' && (
                    <Popup
                      content="View panel spec"
                      size="mini"
                      position="top center"
                      trigger={
                        <LegacyWBIcon
                          name="show"
                          aria-label="View panel spec"
                          className="panel-action"
                          onClick={() =>
                            setPanelPopupMode(PanelPopupMode.VIEW_SPEC)
                          }
                        />
                      }
                    />
                  )}
                  {/* View full screen */}
                  {!isMobile() && !isFullScreenPanelPage && (
                    <StyledButtonWrapper
                      title="View fullscreen"
                      className="panel-action">
                      <Button
                        className="text-moon-600"
                        icon="full-screen-mode-expand"
                        variant="ghost"
                        aria-label="View full screen"
                        size="small"
                        tooltip={'View full screen'}
                        tooltipProps={{className: 'py-8 px-12'}}
                        onClick={() => {
                          openPanelEditor();
                          trackPanelControls({
                            action: 'View full screen clicked',
                          });
                        }}
                      />
                    </StyledButtonWrapper>
                  )}
                  {/* Edit panel */}
                  {!isReadOnly &&
                    !isMobile() &&
                    !panelSpec.noEditMode &&
                    (panelType !== 'Vega' || vegaPanelEnabled) && (
                      <StyledButtonWrapper
                        className="panel-action hide-in-hidden-panels"
                        data-test="edit-panel-button">
                        <Button
                          icon="settings"
                          variant="ghost"
                          aria-label="Edit panel"
                          size={isFullScreenPanelPage ? 'medium' : 'small'}
                          tooltip={'Edit panel'}
                          tooltipProps={{className: 'py-8 px-12'}}
                          onClick={() => {
                            if (
                              isMediaPanelSettingsDrawerEnabled &&
                              !isReport &&
                              getIsMediaPanelSettingsDrawerSupportedForPanel() // Lazily call this since we only need to know once the Edit panel is clicked
                            ) {
                              // open it in full screen mode if it's enabled so that the panel is larger when making edits
                              if (!isFullScreenPanelPage) {
                                openPanelEditor();
                              }
                              openWorkspaceDrawer?.(
                                'media-settings',
                                panelBankSectionConfigRef,
                                panelRef
                              );
                            } else {
                              setPanelPopupMode(PanelPopupMode.EDIT);
                            }
                            trackPanelControls({action: 'Edit panel clicked'});
                          }}
                        />
                      </StyledButtonWrapper>
                    )}
                  {isReadOnly && shouldTurnOnComments && (
                    <Button
                      icon="forum-chat-bubble"
                      tooltip={'Add a comment'}
                      tooltipProps={{className: 'py-8 px-12'}}
                      variant="ghost"
                      size="small"
                      name="chat"
                      title="Add comment"
                      aria-label="Add comment"
                      className="mr-8"
                      onClick={addComment}
                    />
                  )}
                  {/* Three-dot popup menu */}
                  {!isReadOnly && (
                    <>
                      {reportID && (
                        <ShareReportTriggerWrapper
                          reportID={reportID}
                          reportName={reportName}
                          unsetReportID={() => setReportID(undefined)}
                          userIsMemberOfTeam={viewerIsMemberOfTeam}
                        />
                      )}
                      {panelBankSectionConfigRef != null && (
                        <PanelActionsMenu
                          panelSpec={panelSpec}
                          panelBankSectionConfigRef={panelBankSectionConfigRef}
                          setExportType={setExportType}
                          setImageExportType={setImageExportType}
                          defaultMoveToSectionRef={defaultMoveToSectionRef}
                          isOpen={isOverflowMenuActive}
                          setIsOpen={setIsOverflowMenuActive}
                          sharePanel={sharePanel}
                        />
                      )}
                      <PanelExportAPI
                        open={exportType === ExportType.API}
                        onClose={unsetExportType}
                      />
                      <PanelExportCSV
                        open={exportType === ExportType.CSV}
                        onClose={unsetExportType}
                      />
                      {imageExportType && (
                        <PanelImageExportModal
                          open={exportType === ExportType.Image}
                          type={imageExportType}
                          onClose={unsetExportType}
                          panelSpec={panelSpec}
                        />
                      )}
                    </>
                  )}
                  {shouldTurnOnComments &&
                    panelCommentCount != null &&
                    panelCommentCount > 0 && (
                      <Popup
                        content="View comments"
                        inverted
                        size="mini"
                        position="top center"
                        trigger={
                          <div
                            className="panel-comments"
                            onClick={openPanelComments}>
                            <span>{panelCommentCount}</span>
                          </div>
                        }
                      />
                    )}
                </div>
              </div>
            </div>
            <div
              className={twMerge(
                'flex flex-col',
                'h-full w-full grow-[1]',
                'justify-center',
                // Applying this to other panels may cause infinite ResizeObserver loop. Please proceed with caution
                // if you are loosening this check.
                (panelType === 'Code Comparer' ||
                  panelType === 'Media Browser' ||
                  panelType === 'Markdown Panel') &&
                  'overflow-auto'
              )}
              // editablePanelDOMID from reportExport.ts, not imported for code-splitting
              id={'editable-panel-' + panel.__id__}>
              {/* TODO - refactor so PanelCompRedux can use Context/Provider */}
              <PanelCompRedux
                configMode={false}
                currentHeight={currentHeight}
                dimensions={undefined as any}
                disableRunLinks={disableRunLinks}
                onContentHeightChange={onContentHeightChange}
                panelRef={panelRef}
                readOnly={isReadOnly}
              />
            </div>
          </div>
        </TailwindContents>
      </SharedPanelStateContextProvider>
    </PanelIDContextProvider>
  );
};

export const EditablePanel = memo(EditablePanelComp, (prevProps, nextProps) => {
  const didPropsOrStateChange = makeDidPropsOrStateChange({
    name: 'EditablePanel',
    deep: ['pageQuery', 'panel'],
    ignoreFunctions: true,
    ignoreJSX: true,
    debug: false,
    verbose: false,
  });

  return didPropsOrStateChange(prevProps, nextProps);
});
